import { Stack } from '@mui/material';
import { planCreationOptionsDialogSelectors } from '@vy/app-selectors';
import React, { useCallback, useState } from 'react';
import { VyDialog } from '../../../../components/new-design/feedback/VyDialog';
import { useDialog } from '../../../../hooks/use-dialog';
import { useVyNavigate } from '../../../../hooks/use-vy-navigate/use-vy-navigate';
import { CreateContractDialog } from '../../GetStartedPlansPage/components/CardsSections/components/CreateContractDialog';
import { PLAN_CREATION_OPTIONS } from './config/plan-creation-options';
import { PlanCreationOptionCard } from './components/PlanCreationOptionCard';
import type { PlanCreationOption } from './types/plan-creation-option';
import { PLAN_CREATE_ROUTE } from '../../../../router/routing-consts';
import { SelectPlanTemplateDialog } from './SelectPlanTemplateDialog';
import { isNullOrUndefined } from '@vy/utils';

type PlanCreationOptionsDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

const OPTION_SELECTOR_MAP: Record<PlanCreationOption, string> = {
  'new-contract': planCreationOptionsDialogSelectors.optionCardNewContract,
  'new-template': planCreationOptionsDialogSelectors.optionCardNewTemplate,
  'choose-template': planCreationOptionsDialogSelectors.optionCardChooseTemplate,
};

export const PlanCreationOptionsDialog: React.FC<PlanCreationOptionsDialogProps> = ({ isOpen, onClose }) => {
  const navigate = useVyNavigate();
  const [selectedOption, setSelectedOption] = useState<PlanCreationOption | null>(null);

  const {
    isDialogOpen: isContractDialogOpen,
    handleOpen: openContractDialog,
    handleClose: closeContractDialog,
  } = useDialog();

  const {
    isDialogOpen: isSelectTemplateDialogOpen,
    handleOpen: openSelectTemplateDialog,
    handleClose: closeSelectTemplateDialog,
  } = useDialog();

  const handleNext = useCallback(() => {
    switch (selectedOption) {
      case 'new-contract': {
        onClose();
        openContractDialog();
        return;
      }
      case 'new-template': {
        onClose();
        navigate(PLAN_CREATE_ROUTE);
        return;
      }
      case 'choose-template': {
        onClose();
        openSelectTemplateDialog();
        return;
      }
      default: {
        return;
      }
    }
  }, [selectedOption, onClose, openContractDialog, navigate, openSelectTemplateDialog]);

  const handleClose = useCallback(() => {
    setSelectedOption('new-template');
    onClose();
  }, [onClose]);

  return (
    <>
      <VyDialog
        open={isOpen}
        onClose={handleClose}
        label="Create a new pricing plan"
        labelDescription="Select a method to get started"
        ctaText="Next"
        cancelButtonText="Cancel"
        onSubmit={handleNext}
        maxWidth="md"
        disableDividers
        cancelButtonProps={{
          sx: {
            padding: '8px 22px',
            borderRadius: '4px',
            backgroundColor: 'rgba(13, 27, 39, 0.08)',
          },
        }}
        ctaButtonProps={{
          disabled: isNullOrUndefined(selectedOption),
          sx: {
            fontSize: '16px',
            padding: '8px 66px',
            borderRadius: '4px',
            fontWeight: 500,
            color: '#FFF',
            backgroundColor: '#0D0D48',
            '&:hover': {
              backgroundColor: '#0D0D48',
            },
          },
          ...{ 'data-vy': planCreationOptionsDialogSelectors.nextButton },
        }}
      >
        <Stack direction="row" spacing={2}>
          {PLAN_CREATION_OPTIONS.map((option) => (
            <PlanCreationOptionCard
              key={option.value}
              option={option}
              selected={selectedOption === option.value}
              onSelect={setSelectedOption}
              selectorAttribute={OPTION_SELECTOR_MAP[option.value]}
            />
          ))}
        </Stack>
      </VyDialog>

      <CreateContractDialog isOpen={isContractDialogOpen} onClose={closeContractDialog} />

      <SelectPlanTemplateDialog isOpen={isSelectTemplateDialogOpen} onClose={closeSelectTemplateDialog} />
    </>
  );
};
